/**
 * Attendance Sparkline (Phase 2 — Step 2.12).
 *
 * SVG sparkline showing attendance percentage over time.
 */

import React, { useMemo } from 'react';
import { computeTotalSessions, getPastSessionCount } from '@abs/shared-logic';
import { AbsenceType } from '@abs/shared-types';
import type { AbsenceEntry, Course } from '@abs/shared-types';

interface AttendanceSparklineProps {
  course: Course;
  entries: AbsenceEntry[];
  width?: number;
  height?: number;
}

export function AttendanceSparkline({
  course,
  entries,
  width = 200,
  height = 40,
}: AttendanceSparklineProps): React.ReactElement {
  const data = useMemo(() => {
    const { sessions } = computeTotalSessions(
      course.meetingPatterns,
      course.sessionOverrides,
      course.startDate,
      course.endDate,
    );
    const pastCount = getPastSessionCount(sessions);
    const pastSessions = sessions.slice(0, pastCount);

    if (pastSessions.length === 0) return { points: [], floorY: 0 };

    const entriesByDate = new Map(entries.map((e) => [e.date, e]));
    const points: { x: number; y: number; pct: number }[] = [];

    let attended = 0;
    const total = pastSessions.length;

    for (let i = 0; i < total; i++) {
      const entry = entriesByDate.get(pastSessions[i].date);
      const isAbsent = entry?.type === AbsenceType.Unexcused ||
                       entry?.type === AbsenceType.Excused;
      if (!isAbsent) attended++;

      const pct = total > 0 ? (attended / (i + 1)) * 100 : 100;
      points.push({
        x: (i / Math.max(1, total - 1)) * (width - 4) + 2,
        y: height - 2 - (pct / 100) * (height - 4),
        pct,
      });
    }

    // Floor line
    const floorPct = course.sessionOverrides.length > 0 ? 80 : 80; // Default floor
    const floorY = height - 2 - (floorPct / 100) * (height - 4);

    return { points, floorY };
  }, [course, entries, width, height]);

  if (data.points.length === 0) {
    return (
      <div className="attendance-sparkline attendance-sparkline--empty">
        <svg width={width} height={height} aria-label="No attendance data yet">
          <text x={width / 2} y={height / 2} textAnchor="middle" fill="var(--color-text-muted)" fontSize={10}>
            No data
          </text>
        </svg>
      </div>
    );
  }

  const pathD = data.points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  const lastPoint = data.points[data.points.length - 1];

  return (
    <div className="attendance-sparkline" role="img" aria-label={`Attendance trend: ${lastPoint.pct.toFixed(1)}%`}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Floor line */}
        <line
          x1={0} y1={data.floorY} x2={width} y2={data.floorY}
          stroke="var(--color-risk-critical)" strokeWidth={1} strokeDasharray="3,3"
          opacity={0.5}
        />
        {/* Sparkline path */}
        <path
          d={pathD}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        {/* Current point */}
        <circle
          cx={lastPoint.x} cy={lastPoint.y} r={2.5}
          fill="var(--color-primary)"
        />
      </svg>
    </div>
  );
}
